import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AliasBadge, StatusBadge, TrustBadge } from '../components/Badges';
import { TrustLevelBadge } from '../components/TrustLevelBadge';
import { Plus, ShoppingCart, Receipt, Tag, TrendingUp, Eye, MessageSquare, ShieldCheck, Crown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Equipment Finance',
  'Business Loans',
  'Asset Finance',
  'Personal Loans',
  'Mortgage',
  'Solar',
  'Insurance',
  'Auto Finance',
];

export default function BuyerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [intelligence, setIntelligence] = useState(null);
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);

  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [criteria, setCriteria] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [volume, setVolume] = useState('');

  useEffect(() => {
    fetchData();
    fetchMembership();
  }, []);

  const fetchMembership = async () => {
    try {
      const res = await api.get('/subscriptions/status');
      setMembership(res.data);
    } catch (e) {
      console.error('Membership fetch error:', e);
    } finally {
      setMembershipLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [reqRes, txnRes, leadsRes, roomsRes, intelRes, matchRes] = await Promise.all([
        api.get('/buyer-requests/mine'),
        api.get('/transactions/my'),
        api.get('/leads'),
        api.get('/rooms/my'),
        api.get('/buyer/intelligence/me').catch(() => ({ data: null })),
        api.get('/buyer/matches').catch(() => ({ data: [] })),
      ]);
      setRequests(reqRes.data);
      setTransactions(txnRes.data);
      setLeads(leadsRes.data);
      setRooms(roomsRes.data || []);
      setIntelligence(intelRes.data);
      setMatches(matchRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const respondToMatch = async (matchId, action) => {
    try {
      await api.post(`/buyer/matches/${matchId}/${action}`);
      toast.success(action === 'accept' ? 'Bid accepted' : 'Bid declined');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/buyer-requests', {
        category_wanted: category,
        location,
        criteria,
        target_price: parseFloat(targetPrice),
        volume_per_week: parseInt(volume),
      });
      toast.success('Lead request posted');
      setOpen(false);
      setCategory(''); setLocation(''); setCriteria(''); setTargetPrice(''); setVolume('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post request');
    }
  };

  const purchasedCount = transactions.filter(t => t.payment_status === 'paid').length;
  const totalSpent = transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <AliasBadge alias={user.alias} />
              <TrustLevelBadge level={user.trust_level || 0} size="sm" />
              <TrustBadge score={user.trust_score} size="sm" />
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Buyer Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Post requests · Browse leads · Track purchases</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="post-request-btn">
                <Plus className="w-4 h-4 mr-2" /> Post Lead Request
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Post Lead Request</DialogTitle>
                <DialogDescription>
                  Sellers will see your request as your alias. Your identity stays private.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory} required>
                    <SelectTrigger data-testid="request-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Australia-wide, NSW, WA" required data-testid="request-location-input" />
                </div>
                <div>
                  <Label>Criteria</Label>
                  <Textarea value={criteria} onChange={(e) => setCriteria(e.target.value)} placeholder="e.g. ABN verified, $25k+ finance, phone verified" required data-testid="request-criteria-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Target Price ($)</Label>
                    <Input type="number" step="0.01" value={targetPrice} onChange={(e) => setTargetPrice(e.target.value)} required data-testid="request-price-input" />
                  </div>
                  <div>
                    <Label>Volume / week</Label>
                    <Input type="number" value={volume} onChange={(e) => setVolume(e.target.value)} required data-testid="request-volume-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="submit-request-btn">Post Request</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Membership gate banner — non-blocking prompt when subscription inactive */}
        {!membershipLoading && !membership?.has_membership && (
          <div
            className="mb-6 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-5 flex flex-wrap items-center gap-4"
            data-testid="membership-inactive-banner"
          >
            <div className="h-11 w-11 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                Activate your Buyer Membership
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded-full font-mono">
                  <AlertTriangle className="w-3 h-3" /> Required
                </span>
              </div>
              <div className="text-sm text-gray-700 mt-0.5">
                $99/month unlocks lead purchases, demand profiles, and Lead Passport visibility. You cannot buy leads without an active membership.
              </div>
            </div>
            <Button
              onClick={() => navigate('/membership')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="activate-membership-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> Activate — $99/mo
            </Button>
          </div>
        )}

        {/* Membership active confirmation (compact) */}
        {!membershipLoading && membership?.has_membership && (
          <div
            className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="membership-active-banner"
          >
            <div className="flex items-center gap-3">
              <Crown className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-gray-800">
                <strong className="font-semibold">Buyer Membership active.</strong> You can purchase leads and post demand.
              </span>
            </div>
            <button
              onClick={() => navigate('/membership')}
              className="text-xs font-mono uppercase tracking-wider text-emerald-700 hover:text-emerald-900"
              data-testid="manage-membership-link"
            >
              Manage
            </button>
          </div>
        )}

        {/* Verification promo (shown until Level 2) */}
        {(user.trust_level || 0) < 2 && (
          <div className="mb-6 border border-[#0055FF] bg-blue-50 rounded-lg p-4 flex items-center gap-4" data-testid="buyer-verify-banner">
            <ShieldCheck className="w-6 h-6 text-[#0055FF] flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-gray-900">Unlock higher purchase limits</div>
              <div className="text-xs text-gray-700 mt-0.5">Complete Identity + Business verification to purchase up to $10,000 per transaction and access premium leads.</div>
            </div>
            <Button onClick={() => navigate('/verification')} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="go-to-verification-btn">
              Verify now
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden mb-8">
          <Stat label="Active Requests" value={requests.length} icon={TrendingUp} />
          <Stat label="Leads Available" value={leads.length} icon={Tag} />
          <Stat label="Purchased" value={purchasedCount} icon={ShoppingCart} />
          <Stat label="Total Spent" value={`$${totalSpent.toFixed(2)}`} icon={Receipt} />
        </div>

        {/* Buyer Intelligence Score */}
        {intelligence && (
          <section className="mb-8" data-testid="buyer-intelligence-card">
            <div className="bg-gray-900 text-white rounded-lg p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] opacity-70 font-mono">Buyer Intelligence</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <div className="font-heading text-5xl font-semibold" data-testid="buyer-intel-score">
                      {intelligence.score}
                    </div>
                    <div className="text-lg opacity-70">/100</div>
                  </div>
                  <div className="mt-1 text-sm opacity-80">{intelligence.label}</div>
                </div>
                <div className="hidden md:block h-16 w-px bg-white/20" />
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="opacity-60">Completed</span><span className="ml-2 font-mono">{intelligence.metrics?.purchases_completed}</span></div>
                  <div><span className="opacity-60">Refund rate</span><span className="ml-2 font-mono">{Math.round((intelligence.metrics?.refund_rate || 0) * 100)}%</span></div>
                  <div><span className="opacity-60">Tenure</span><span className="ml-2 font-mono">{intelligence.metrics?.marketplace_tenure_days}d</span></div>
                  <div><span className="opacity-60">Reliability</span><span className="ml-2 font-mono">{Math.round((intelligence.metrics?.payment_reliability || 0) * 100)}%</span></div>
                </div>
              </div>
              <div className="text-xs opacity-70 max-w-xs">
                Deterministic score computed from your marketplace activity. Anonymous to sellers — only your alias and score are shown.
              </div>
            </div>
          </section>
        )}

        {/* My Requests */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">My Lead Requests</h2>
          {requests.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
              No requests yet. Post one to let sellers know what you&apos;re looking for.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-requests-grid">
              {requests.map(req => (
                <div key={req.request_id} className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading text-base font-semibold text-gray-900">{req.category_wanted}</h3>
                    <StatusBadge status={req.status} />
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{req.location}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Volume</span><span className="font-mono">{req.volume_per_week}/week</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Target</span><span className="font-mono">${req.target_price.toFixed(2)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bids Received — sellers who responded to my requests */}
        <section className="mb-12" data-testid="buyer-bids-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-gray-900">Bids Received</h2>
            <span className="text-xs uppercase tracking-wider text-gray-500 font-mono">
              {matches.filter(m => m.status === 'bid_placed').length} awaiting response
            </span>
          </div>
          {matches.filter(m => m.bid_price != null).length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500 text-sm">
              No bids yet. Sellers matching your requests will show up here.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {matches
                .filter(m => m.bid_price != null)
                .sort((a, b) => {
                  // bid_placed first, then accepted, then declined
                  const rank = { bid_placed: 0, accepted: 1, declined: 2 };
                  return (rank[a.status] ?? 3) - (rank[b.status] ?? 3);
                })
                .map(m => (
                <div
                  key={m.match_id}
                  data-testid={`bid-card-${m.match_id}`}
                  className="bg-white border border-gray-200 rounded-lg p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <AliasBadge alias={m.seller_alias} />
                    <StatusBadge status={m.status} />
                  </div>
                  <h3 className="font-heading text-base font-semibold mb-3">
                    Bid on: {m.request_snapshot?.category_wanted}
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Your target</span><span className="font-mono">${Number(m.request_snapshot?.target_price || 0).toFixed(2)}</span></div>
                    {m.bid_price != null && (
                      <div className="flex justify-between text-[#0055FF]"><span>Seller bid</span><span className="font-mono font-semibold">${Number(m.bid_price).toFixed(2)}</span></div>
                    )}
                  </div>
                  {m.bid_note && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
                      &ldquo;{m.bid_note}&rdquo;
                    </div>
                  )}
                  {m.status === 'bid_placed' && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-[#0055FF] hover:bg-[#0044CC] text-white"
                        onClick={() => respondToMatch(m.match_id, 'accept')}
                        data-testid={`accept-bid-btn-${m.match_id}`}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => respondToMatch(m.match_id, 'decline')}
                        data-testid={`decline-bid-btn-${m.match_id}`}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                  {m.status === 'accepted' && m.room_id && (
                    <div className="mt-4">
                      <Button
                        size="sm"
                        className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
                        onClick={() => navigate(`/rooms/${m.room_id}`)}
                        data-testid={`open-room-btn-${m.match_id}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Open Deal Room
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Available leads */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-gray-900">Available Leads</h2>
            <Button variant="outline" onClick={() => navigate('/marketplace')} data-testid="view-all-leads-btn">
              View Marketplace
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {leads.slice(0, 6).map(lead => (
              <div
                key={lead.lead_id}
                onClick={() => navigate(`/leads/${lead.lead_id}`)}
                className="bg-white border border-gray-200 rounded-lg p-5 cursor-pointer card-hover"
                data-testid={`buyer-lead-${lead.lead_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <AliasBadge alias={lead.seller_alias} />
                  <span className="font-mono text-xs text-gray-400">{lead.lead_id}</span>
                </div>
                <h3 className="font-heading text-base font-semibold mb-2">{lead.category}</h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{lead.region}</span>
                  <span className="font-mono font-semibold">${lead.price.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Deal Rooms */}
        {rooms.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">My Deal Rooms</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="buyer-rooms-list">
              {rooms.map(r => (
                <button
                  key={r.room_id}
                  onClick={() => navigate(`/rooms/${r.room_id}`)}
                  className="bg-white border border-gray-200 rounded-lg p-4 text-left card-hover"
                  data-testid={`buyer-room-${r.room_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <AliasBadge alias={r.seller_alias} />
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="font-mono text-xs text-gray-500">{r.lead_id}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#0055FF]">
                    <MessageSquare className="w-3 h-3" /> Open Deal Room
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-white p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
    </div>
    <div className="font-mono text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);
